'use strict';

import Swiper from 'swiper';
import { Pagination, EffectFade } from 'swiper/modules';
import { determineService } from './header-video';

function createImagePlayer(duration) {
    let startTime = 0;
    let elapsed = 0;
    let timer = null;
    let _paused = true;
    const events = {};

    const _emit = (event, data) => {
        (events[event] || []).forEach(cb => cb(data));
    };

    const tick = () => {
        if (_paused) return;
        const delta = Date.now() - startTime;
        const percent = Math.min(((elapsed + delta) / (duration * 1000)) * 100, 100);
        _emit('progress', percent);
        if (percent < 100) {
            requestAnimationFrame(tick);
        } else {
            _emit('ended');
        }
    };

    return {
        async play() {
            if (!_paused) return;
            _paused = false;
            startTime = Date.now();
            timer = setTimeout(() => _emit('ended'), (duration * 1000) - elapsed);
            tick();
            _emit('play');
        },
        async pause() {
            if (_paused) return;
            _paused = true;
            elapsed += Date.now() - startTime;
            clearTimeout(timer);
            _emit('pause');
        },
        async stop() {
            await this.pause();
            elapsed = 0;
        },
        async reset() {
            elapsed = 0;
            startTime = 0;
            clearTimeout(timer);
            _paused = true;
        },
        async isPlaying() {
            return !_paused;
        },
        on(event, callback) {
            (events[event] ||= []).push(callback);
        },
        off(event, callback) {
            if (!events[event]) return;
            events[event] = events[event].filter(cb => cb !== callback);
        },
    };
}

function initialiseSlideVideo(slide, imageDuration) {
    const container = slide.querySelector('.video-container');
    if (!container) return null;

    const url = container.dataset.videoUrl;
    if (!url) return null;

    const provider = determineService(url);
    if (!provider) return null;

    const videoPlayer = provider.player();

    return videoPlayer.mount(container).then(() => {
        container.classList.add('is-loaded');
        slide.classList.add('is-loaded');

        // Wrap the video player with a timer-driven proxy so the progress
        // circle animates and the slide advances even if autoplay is blocked.
        const timer = createImagePlayer(imageDuration);
        const proxy = {
            async play() {
                videoPlayer.play().catch(() => {});
                await timer.play();
            },
            async pause() {
                videoPlayer.pause().catch(() => {});
                await timer.pause();
            },
            async stop() {
                videoPlayer.stop().catch(() => {});
                await timer.stop();
            },
            async reset() {
                videoPlayer.stop().catch(() => {});
                await timer.reset();
            },
            async isPlaying() { return timer.isPlaying(); },
            on(event, callback) { timer.on(event, callback); },
            off(event, callback) { timer.off(event, callback); },
        };

        return proxy;
    });
}

export function initialiseElement(element) {
    const scrollerElement = element.querySelector('.swiper-container');

    if (!scrollerElement) return;

    const paginationElement = element.querySelector('.swiper-pagination');
    const progressCircle = element.querySelector('.autoplay-progress__foreground');
    const imageDuration = parseInt(element.dataset.swiperDelay, 10) || 5;
    const totalSlides = scrollerElement.querySelectorAll('.swiper-slide').length;

    const swiperInstance = new Swiper(scrollerElement, {
        slidesPerView: 1,
        grabCursor: false,
        effect: 'fade',
        crossFade: true,
        loop: false,
        autoplay: false,
        modules: [Pagination, EffectFade],
        pagination: paginationElement ? {
            el: paginationElement,
            type: 'bullets',
            bulletElement: 'button',
            clickable: true,
            renderBullet(index, className) {
                return `<button class="${className}"><span class="visually-hidden">Slide ${index + 1} of ${totalSlides}</span></button>`;
            },
        } : false,
        on: {
            init(s) {
                for (const slide of s.slides) {
                    if (slide.classList.contains('home-hero__slide--video')) {
                        const videoPromise = initialiseSlideVideo(slide, imageDuration);
                        slide._loaded = (videoPromise || Promise.resolve(null)).then(player => {
                            slide._player = player || createImagePlayer(imageDuration);
                            return true;
                        });
                    } else {
                        slide._loaded = new Promise(resolve => {
                            slide._player = createImagePlayer(imageDuration);
                            resolve(true);
                        });
                    }
                }
            },
        },
    });

    const playButtons = [...element.querySelectorAll('.home-hero__stop-autoplay')];
    let paused = false;

    function setProgress(percent) {
        if (progressCircle) {
            progressCircle.style.setProperty('--progress', percent);
        }
    }

    const toggleButtonState = isPlaying => {
        for (const btn of playButtons) {
            const icon = btn.querySelector('.home-hero__icon');
            const label = btn.querySelector('.home-hero__label');
            if (isPlaying) {
                icon.innerHTML = '<svg class="o-icon o-icon--pause"><use xlink:href="#pause"></use></svg>';
                label.textContent = 'Pause';
            } else {
                icon.innerHTML = '<svg class="o-icon o-icon--play"><use xlink:href="#play"></use></svg>';
                label.textContent = 'Play';
            }
        }
    };

    async function startAutoplay(fromStart = true) {
        const slide = swiperInstance.slides[swiperInstance.activeIndex];
        await slide._loaded;

        slide._player._cleanup?.();
        slide._player._cleanup = null;
        if (fromStart) slide._player.reset();
        toggleButtonState(true);

        const onProgress = percent => setProgress(percent);
        const onEnded = () => {
            cleanup();
            if (swiperInstance.isEnd) {
                swiperInstance.slideTo(0);
            } else {
                swiperInstance.slideNext();
            }
        };

        slide._player.on('progress', onProgress);
        slide._player.on('ended', onEnded);
        slide._player.play();

        function cleanup() {
            slide._player.off('progress', onProgress);
            slide._player.off('ended', onEnded);
            slide._player._cleanup = null;
        }

        slide._player._cleanup = cleanup;
    }

    swiperInstance.on('slideChangeTransitionStart', () => {
        const prev = swiperInstance.slides[swiperInstance.previousIndex];
        if (!prev) return;
        prev._player?._cleanup?.();
        if (prev._player) prev._player._cleanup = null;
        prev._player?.pause();
        prev._player?.reset();
        setProgress(0);
    });

    swiperInstance.on('slideChange', () => {
        if (!paused) {
            startAutoplay();
        }
    });

    async function pause() {
        if (paused) return;
        paused = true;
        const slide = swiperInstance.slides[swiperInstance.activeIndex];
        await slide._loaded;
        slide._player?.pause();
    }

    async function resume() {
        if (!paused) return;
        paused = false;
        return startAutoplay(false);
    }

    for (const btn of playButtons) {
        btn.addEventListener('click', () => {
            if (paused) {
                resume();
                toggleButtonState(true);
            } else {
                pause();
                toggleButtonState(false);
            }
        });
    }

    startAutoplay();
}

export function init() {
    document.querySelectorAll('.js-home-hero').forEach(initialiseElement);
}
